import { MouseEvent, useLayoutEffect, useRef, useState } from 'react';
import { useSpring } from 'react-spring';

import { usePrevious } from '../../hooks/use-previous';
import { determineFurthestFromViewportRectangle } from '../../utils/determineFurthestFromViewportRectangle';
import { MapSpring } from './types';
import { X_GAP } from './use-mindmap';
import { Positioning } from './use-mindmap-positioning';

type MapCoordsToPageClientCoordsOptions = {
  styleX?: number;
  styleY?: number;
  styleScale?: number;
};

export type UpdateStyleOptions = {
  x?: number;
  y?: number;
  immediate?: boolean;
  scale?: number;
  animate?: boolean;
};

type ScaleOptions = {
  toPageCoords: {
    x: number;
    y: number;
  };
  enlarge?: boolean;
  newScale?: number;
  scaleBy?: number;
};

export interface InteractableMap {
  interactableMapSpring: MapSpring | undefined;
  mindmapHeight: number | undefined;
  mindmapWidth: number | undefined;
  resetScaling: () => void;
  setMousePosition: (pageX: number, pageY: number) => void;
  scale: (options: ScaleOptions) => void;
  mapCoordsToPageClientCoords: (
    mapX: number,
    mapY: number,
    options?: MapCoordsToPageClientCoordsOptions,
  ) => { x: number; y: number } | undefined;
  pageClientCoordsToMapCoords: (pageClientX: number, pageClientY: number) => { x: number; y: number } | undefined;
  updateStyle: (options?: UpdateStyleOptions) => void;
  toggleContextMenu: (e: MouseEvent) => void;
  positionContextMenu: (pageCoords: { pageX: number; pageY: number }, animate?: boolean) => void;
  hideContextMenu: () => void;
  moveContextMenuMapCoords: (nodeXDiff: number, nodeYDiff: number) => void;
  contextMenuOpen: boolean;
  contextMenuRef: React.RefObject<HTMLDivElement>;
}

type UseInteractableMap = { positioning: Positioning };

let mousePageX = window.innerWidth / 2;
let mousePageY = window.innerHeight / 2;

export const useInteractableMap = ({ positioning: { boundingBox } }: UseInteractableMap): InteractableMap => {
  const interactableMapSpring = useSpring(() => ({
    x: 0,
    y: 0,
    scale: 1,
    cursor: 'auto',
    isNodeDragging: false,
    isDraggingMap: false,
    toolPanelPageX: 0,
    toolPanelPageY: 0,
  }));
  const [contextMenuMapCoords, setContextMenuMapCoords] = useState<{ x: number; y: number } | undefined>();
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const [style, api] = interactableMapSpring;
  const contextMenuOpen = !!contextMenuMapCoords;

  let mindmapHeight: number | undefined = undefined;
  let mindmapWidth: number | undefined = undefined;
  if (boundingBox) {
    mindmapHeight = (boundingBox.bottom - boundingBox.top) * 2;
    mindmapWidth = (boundingBox.right - boundingBox.left) * 2;
  }

  const resetScaling = () => {
    // scale to last mouse coords
    scale({ toPageCoords: { x: mousePageX, y: mousePageY }, newScale: 1 });
  };

  const setMousePosition = (pageX: number, pageY: number) => {
    mousePageX = pageX;
    mousePageY = pageY;
  };

  const scale = ({ toPageCoords, enlarge, scaleBy, newScale }: ScaleOptions) => {
    if (mindmapWidth != null && mindmapHeight != null) {
      const clientX = toPageCoords.x - mindmapWidth / 2;
      const clientY = toPageCoords.y - mindmapHeight / 2;
      if (!scaleBy) scaleBy = 0.9;
      const oldScale = style.scale.get();
      const mousePointTo = {
        x: clientX / oldScale - style.x.get() / oldScale,
        y: clientY / oldScale - style.y.get() / oldScale,
      };
      if (!newScale) newScale = enlarge ? oldScale * scaleBy : oldScale / scaleBy;
      if (newScale !== 0) {
        const styleX = -(mousePointTo.x - clientX / newScale) * newScale;
        const styleY = -(mousePointTo.y - clientY / newScale) * newScale;
        updateStyle({ x: styleX, y: styleY, immediate: true, scale: newScale });
      }
    }
  };

  const [prevMapWidth, prevMapHeight] = usePrevious([mindmapWidth, mindmapHeight]) || [];

  // on map dimensions change: recalculate and add the scaled width and height
  // changes to the map position before rendering to prevent the map itself moving on changes
  useLayoutEffect(() => {
    if (prevMapWidth && prevMapHeight && style && mindmapWidth && mindmapHeight && api) {
      const scalePositionMult = (style.scale.goal - 1) / 2;

      api.set({
        x: style.x.goal + (mindmapWidth - prevMapWidth) * scalePositionMult,
        y: style.y.goal + (mindmapHeight - prevMapHeight) * scalePositionMult,
      });
    }
  }, [boundingBox]);

  /**
   * Converts page client coordinates (e.g. the x-range from 0 to 1920 and the y-range from 0 to 1080)
   * to the corresponding coordinates on the interactable map while taking scaling and map positioning into account.
   * @param pageClientX The page client x coordinate / screen/window coordinate (e.g. event.clientX)
   * @param pageClientY The page client y coordinate / screen/window coordinate (e.g. event.clientY)
   * @returns The converted coordinates in relation to the map top left corner (map top left corner = 0/0)
   */
  const pageClientCoordsToMapCoords = (pageClientX: number, pageClientY: number) => {
    if (mindmapWidth && mindmapHeight) {
      const clientX = pageClientX - mindmapWidth / 2;
      const clientY = pageClientY - mindmapHeight / 2;
      const oldScale = style.scale.goal;
      const cursorRelToMapCenter = {
        x: clientX / oldScale - style.x.goal / oldScale,
        y: clientY / oldScale - style.y.goal / oldScale,
      };
      const cursorRelToMapTopLeft = {
        x: cursorRelToMapCenter.x + mindmapWidth / 2,
        y: cursorRelToMapCenter.y + mindmapHeight / 2,
      };

      return { x: cursorRelToMapTopLeft.x + X_GAP / 2, y: cursorRelToMapTopLeft.y };
    }
  };

  /**
   * Converts layout map coordinates (= map coordinates without any positioning/scaling
   * applied to them) to page client coordinates.
   * @param mapX The map x coordinate.
   * @param mapY The map y coordinate.
   * @returns The converted coordinates in relation to the page top left corner.
   */
  const mapCoordsToPageClientCoords = (mapX: number, mapY: number, options?: MapCoordsToPageClientCoordsOptions) => {
    if (mindmapWidth && mindmapHeight) {
      const styleX = options?.styleX || style.x.goal;
      const styleY = options?.styleY || style.y.goal;
      const styleScale = options?.styleScale || style.scale.goal;

      const cursorRelToMapTopLeft = {
        x: mapX - X_GAP / 2,
        y: mapY,
      };
      const cursorRelToMapCenter = {
        x: cursorRelToMapTopLeft.x - mindmapWidth / 2,
        y: cursorRelToMapTopLeft.y - mindmapHeight / 2,
      };
      const scale = styleScale;
      const clientX = (cursorRelToMapCenter.x + styleX / scale) * scale;
      const clientY = (cursorRelToMapCenter.y + styleY / scale) * scale;
      const pageClientX = clientX + mindmapWidth / 2;
      const pageClientY = clientY + mindmapHeight / 2;

      return { x: pageClientX, y: pageClientY };
    }
  };

  const determineToolPanelCoordsInViewport = (x?: number, y?: number, scale?: number) => {
    if (contextMenuOpen) {
      const toolPanelPageCoords = mapCoordsToPageClientCoords(contextMenuMapCoords.x, contextMenuMapCoords.y, {
        styleX: x,
        styleY: y,
        styleScale: scale,
      });

      const toolPanel = contextMenuRef.current;
      const toolPanelWidth = toolPanel?.clientWidth;
      const toolPanelHeight = toolPanel?.clientHeight;

      if (toolPanelWidth && toolPanelHeight && toolPanelPageCoords) {
        const { moveXAmount, moveYAmount } = determineFurthestFromViewportRectangle([
          {
            left: toolPanelPageCoords.x,
            right: toolPanelPageCoords.x + toolPanelWidth,
            bottom: toolPanelPageCoords.y + toolPanelHeight,
            top: toolPanelPageCoords.y,
          },
        ]);

        return { x: toolPanelPageCoords?.x + moveXAmount, y: toolPanelPageCoords?.y + moveYAmount };
      }
    }
  };

  const prevContextMenuMapCoords = usePrevious(contextMenuMapCoords);
  const [preventNextAnimate, setPreventNextAnimate] = useState<boolean>(false);
  useLayoutEffect(() => {
    if (contextMenuOpen) {
      updateStyle({ animate: !preventNextAnimate && !!prevContextMenuMapCoords });
      setPreventNextAnimate(false);
    }
  }, [contextMenuMapCoords]);

  const updateStyle = ({ x, y, immediate, scale, animate }: UpdateStyleOptions = {}) => {
    if (contextMenuOpen) {
      // update tool panel page coords as well
      const toolPanelCoords = determineToolPanelCoordsInViewport(x, y, scale);
      if (toolPanelCoords) {
        const newStyle = { toolPanelPageX: toolPanelCoords.x, toolPanelPageY: toolPanelCoords.y, x, y, scale };
        if (animate) api.start({ ...newStyle, immediate: false });
        else api.set(newStyle);
      }
    } else api.start({ x, y, scale, immediate: immediate });
  };

  const toggleContextMenu = (e: MouseEvent) => {
    if (contextMenuOpen) setContextMenuMapCoords(undefined);
    else positionContextMenu(e);
  };

  const positionContextMenu = ({ pageX, pageY }: { pageX: number; pageY: number }, animate: boolean = true) => {
    const mapCoords = pageClientCoordsToMapCoords(pageX, pageY);
    setContextMenuMapCoords(mapCoords);
    if (!animate) setPreventNextAnimate(true);
  };

  const hideContextMenu = () => {
    if (contextMenuOpen) setContextMenuMapCoords(undefined);
  };

  const moveContextMenuMapCoords = (nodeXDiff: number, nodeYDiff: number) => {
    if (contextMenuOpen)
      setContextMenuMapCoords({ x: contextMenuMapCoords.x + nodeXDiff, y: contextMenuMapCoords.y + nodeYDiff });
  };

  return {
    interactableMapSpring,
    resetScaling,
    setMousePosition,
    scale,
    mindmapHeight,
    mindmapWidth,
    mapCoordsToPageClientCoords,
    pageClientCoordsToMapCoords,
    updateStyle,
    toggleContextMenu,
    hideContextMenu,
    moveContextMenuMapCoords,
    contextMenuOpen,
    contextMenuRef,
    positionContextMenu,
  };
};
